import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Award, Settings as SettingsIcon, ListChecks, Trash2, Plus, Save,
  PlayCircle, ClipboardEdit, UserCheck, ShieldCheck, AlertOctagon,
  Trophy, Star, BarChart3, FileText, Download, RefreshCw,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend,
} from "recharts";

/**
 * Phase 5L — Appraisal Admin Hub
 * Tabs: Settings · Questions · Cycles · Mistake Log · Appreciations
 */
export default function AppraisalAdmin() {
  const [tab, setTab] = useState("settings");
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <Award className="h-3 w-3" /> Phase 5L · Performance Management
        </div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Appraisal Hub
        </h1>
        <p className="text-xs text-slate-500 mt-0.5">
          Configure cycles, questions, mistake logs, and review pending appraisals across the organization.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-red-50 border border-red-200">
          <TabsTrigger value="settings" data-testid="tab-settings"><SettingsIcon className="h-3.5 w-3.5 mr-1" />Settings</TabsTrigger>
          <TabsTrigger value="questions" data-testid="tab-questions"><ListChecks className="h-3.5 w-3.5 mr-1" />Questions</TabsTrigger>
          <TabsTrigger value="cycles" data-testid="tab-cycles"><ClipboardEdit className="h-3.5 w-3.5 mr-1" />Cycles</TabsTrigger>
          <TabsTrigger value="mistakes" data-testid="tab-mistakes"><AlertOctagon className="h-3.5 w-3.5 mr-1" />Mistake Log</TabsTrigger>
          <TabsTrigger value="appreciations" data-testid="tab-appreciations"><Trophy className="h-3.5 w-3.5 mr-1" />Appreciations</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics"><BarChart3 className="h-3.5 w-3.5 mr-1" />Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="mt-4"><SettingsTab /></TabsContent>
        <TabsContent value="questions" className="mt-4"><QuestionsTab /></TabsContent>
        <TabsContent value="cycles" className="mt-4"><CyclesTab /></TabsContent>
        <TabsContent value="mistakes" className="mt-4"><MistakesTab /></TabsContent>
        <TabsContent value="appreciations" className="mt-4"><AppreciationsTab /></TabsContent>
        <TabsContent value="analytics" className="mt-4"><AnalyticsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
function SettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    const { data } = await api.get("/appraisal/settings");
    setS(data);
  };
  useEffect(() => { load(); }, []);
  if (!s) return <div className="text-sm text-slate-500">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/appraisal/settings", {
        active: s.active,
        cycle_name: s.cycle_name,
        auto_trigger_on_anniversary: s.auto_trigger_on_anniversary,
        anniversary_window_days: parseInt(s.anniversary_window_days, 10) || 30,
      });
      toast.success(s.active ? "Appraisal cycle ACTIVE — employees can see portal" : "Appraisal cycle disabled");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-lg p-5 space-y-4" data-testid="settings-card">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-semibold">Master Appraisal Toggle</Label>
          <p className="text-xs text-slate-600">When ON, employees see the Appraisal section in their portal.</p>
        </div>
        <Switch checked={s.active} onCheckedChange={(v) => setS({ ...s, active: v })} data-testid="active-switch" />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-600">Cycle Name</Label>
        <Input value={s.cycle_name || ""} onChange={(e) => setS({ ...s, cycle_name: e.target.value })}
                placeholder="e.g. FY 2026-27 Annual" data-testid="cycle-name-input" />
      </div>
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm">Auto-trigger on joining anniversary</Label>
          <p className="text-xs text-slate-500">Daily cron will start a cycle for every employee within the window below.</p>
        </div>
        <Switch checked={s.auto_trigger_on_anniversary}
                  onCheckedChange={(v) => setS({ ...s, auto_trigger_on_anniversary: v })}
                  data-testid="auto-switch" />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-600">Anniversary window (days)</Label>
        <Input type="number" value={s.anniversary_window_days}
                onChange={(e) => setS({ ...s, anniversary_window_days: e.target.value })}
                className="w-32" data-testid="window-input" />
      </div>
      <Button onClick={save} disabled={saving} className="brand-bg brand-bg-hover text-white" data-testid="settings-save">
        <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
function QuestionsTab() {
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    const { data } = await api.get("/appraisal/questions");
    setQuestions(data.questions || []);
  };
  useEffect(() => { load(); }, []);

  const addRow = () => setQuestions([...questions, {
    id: null, category: "", text: "", weight: 1.0, sort_order: questions.length + 1,
  }]);
  const updRow = (i, patch) => {
    const next = [...questions];
    next[i] = { ...next[i], ...patch };
    setQuestions(next);
  };
  const delRow = (i) => setQuestions(questions.filter((_, idx) => idx !== i));

  const save = async () => {
    const cleaned = questions.filter(q => q.category?.trim() && q.text?.trim());
    if (cleaned.length === 0) { toast.error("Add at least one question"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/appraisal/questions", { questions: cleaned });
      toast.success(`Saved ${data.count} questions`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="questions-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-600">Categorize and weight every question. Edit/rearrange any time; in-flight cycles keep their snapshot.</p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={addRow} data-testid="add-question"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
          <Button size="sm" onClick={save} disabled={saving} className="brand-bg brand-bg-hover text-white" data-testid="save-questions">
            <Save className="h-3.5 w-3.5 mr-1" />{saving ? "Saving…" : "Save All"}
          </Button>
        </div>
      </div>
      <div className="border border-slate-200 rounded overflow-hidden bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-2 py-1.5 font-semibold w-32">Category</th>
              <th className="text-left px-2 py-1.5 font-semibold">Question</th>
              <th className="text-left px-2 py-1.5 font-semibold w-20">Weight</th>
              <th className="text-left px-2 py-1.5 font-semibold w-16">Order</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {questions.map((q, i) => (
              <tr key={q.id || i} className="hover:bg-red-50/30">
                <td className="px-1 py-1">
                  <Input value={q.category} onChange={(e) => updRow(i, { category: e.target.value })} className="h-7 text-xs" data-testid={`q-cat-${i}`} />
                </td>
                <td className="px-1 py-1">
                  <Input value={q.text} onChange={(e) => updRow(i, { text: e.target.value })} className="h-7 text-xs" data-testid={`q-text-${i}`} />
                </td>
                <td className="px-1 py-1">
                  <Input type="number" step="0.1" value={q.weight} onChange={(e) => updRow(i, { weight: parseFloat(e.target.value) || 0 })} className="h-7 text-xs" />
                </td>
                <td className="px-1 py-1">
                  <Input type="number" value={q.sort_order} onChange={(e) => updRow(i, { sort_order: parseInt(e.target.value, 10) || 0 })} className="h-7 text-xs" />
                </td>
                <td className="px-1 py-1 text-center">
                  <button onClick={() => delRow(i)} className="text-red-500 hover:text-red-700" data-testid={`del-q-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
            {questions.length === 0 && (
              <tr><td colSpan={5} className="text-center text-slate-400 py-6">No questions defined</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function CyclesTab() {
  const [cycles, setCycles] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkCycle, setBulkCycle] = useState("");
  const [starting, setStarting] = useState(false);

  const load = async () => {
    const q = filterStatus ? `?status=${filterStatus}` : "";
    const { data } = await api.get(`/appraisal/cycles${q}`);
    setCycles(data);
  };
  useEffect(() => { load(); }, [filterStatus]);

  const startBulk = async () => {
    if (!bulkCycle.trim()) { toast.error("Cycle name required"); return; }
    setStarting(true);
    try {
      const { data } = await api.post("/appraisal/cycles/start-bulk", { cycle_name: bulkCycle.trim() });
      toast.success(`Created ${data.created} cycles · skipped ${data.skipped} (already exist)`);
      setShowBulkDialog(false); setBulkCycle("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk start failed");
    } finally { setStarting(false); }
  };

  return (
    <div className="space-y-3" data-testid="cycles-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-600">Filter:</Label>
          <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56 h-8 text-xs"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="DRAFT">Draft (Employee filling)</SelectItem>
              <SelectItem value="PENDING_HOD_REVIEW">Pending HOD Review</SelectItem>
              <SelectItem value="PENDING_MGT_APPROVAL">Pending Mgmt Approval</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="LETTER_ISSUED">Letter Issued</SelectItem>
              <SelectItem value="ACCEPTED">Accepted</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setShowBulkDialog(true)} className="brand-bg brand-bg-hover text-white" data-testid="start-bulk">
          <PlayCircle className="h-4 w-4 mr-1.5" /> Bulk Start Cycle
        </Button>
      </div>

      <div className="border border-slate-200 rounded bg-white overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Employee</th>
              <th className="text-left px-3 py-2 font-semibold">Cycle</th>
              <th className="text-left px-3 py-2 font-semibold">Department</th>
              <th className="text-left px-3 py-2 font-semibold">Status</th>
              <th className="text-left px-3 py-2 font-semibold">Submitted</th>
              <th className="text-left px-3 py-2 font-semibold">HOD Score</th>
              <th className="text-left px-3 py-2 font-semibold">Final Hike</th>
              <th className="text-left px-3 py-2 font-semibold">Letter</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {cycles.map((c) => (
              <tr key={c.id} className="hover:bg-red-50/30" data-testid={`cycle-row-${c.id}`}>
                <td className="px-3 py-1.5">
                  <div className="font-medium">{c.employee_name}</div>
                  <div className="text-[10px] text-slate-500">{c.employee_code}</div>
                </td>
                <td className="px-3 py-1.5 text-slate-700">{c.cycle_name}</td>
                <td className="px-3 py-1.5 text-slate-600">{c.department_name || "—"}</td>
                <td className="px-3 py-1.5"><StatusBadge status={c.status} /></td>
                <td className="px-3 py-1.5 text-slate-500">{c.submitted_at ? new Date(c.submitted_at).toLocaleDateString() : "—"}</td>
                <td className="px-3 py-1.5 font-mono">{c.hod_review?.weighted_score ?? "—"}</td>
                <td className="px-3 py-1.5 font-mono text-emerald-700">{c.mgt_approval?.final_hike_pct ? `${c.mgt_approval.final_hike_pct}%` : "—"}</td>
                <td className="px-3 py-1.5">
                  <LetterActions cycle={c} onUpdated={load} />
                </td>
              </tr>
            ))}
            {cycles.length === 0 && (
              <tr><td colSpan={8} className="text-center text-slate-400 py-8">No cycles in this filter</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bulk Start Appraisal Cycle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs">Cycle Name</Label>
            <Input value={bulkCycle} onChange={(e) => setBulkCycle(e.target.value)} placeholder="e.g. FY 2026-27 Annual" data-testid="bulk-cycle-input" />
            <p className="text-[11px] text-slate-500">Creates a cycle for every Working employee. Existing cycles with the same name are skipped (idempotent).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDialog(false)}>Cancel</Button>
            <Button onClick={startBulk} disabled={starting} className="brand-bg brand-bg-hover text-white" data-testid="confirm-bulk">
              {starting ? "Starting…" : "Start All"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    DRAFT: "bg-slate-100 text-slate-700",
    PENDING_HOD_REVIEW: "bg-amber-100 text-amber-800",
    PENDING_MGT_APPROVAL: "bg-blue-100 text-blue-800",
    APPROVED: "bg-emerald-100 text-emerald-800",
    LETTER_ISSUED: "bg-violet-100 text-violet-800",
    ACCEPTED: "bg-teal-100 text-teal-800",
  };
  return <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${map[status] || "bg-slate-100 text-slate-700"}`}>{status?.replace(/_/g, " ")}</span>;
}

// ---------------------------------------------------------------------------
// Letter actions per cycle row
function LetterActions({ cycle, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const status = cycle.status;
  const canIssue = status === "APPROVED";
  const hasLetter = status === "LETTER_ISSUED" || status === "ACCEPTED";

  const issue = async () => {
    if (!window.confirm(`Generate appraisal letter for ${cycle.employee_name}?`)) return;
    setBusy(true);
    try {
      await api.post(`/appraisal/cycles/${cycle.id}/letter`);
      toast.success("Letter generated");
      onUpdated && onUpdated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed");
    } finally { setBusy(false); }
  };

  const download = async () => {
    try {
      const resp = await api.get(`/appraisal/cycles/${cycle.id}/letter`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `AppraisalLetter_${cycle.employee_code}_${(cycle.cycle_name || "").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  if (canIssue) {
    return (
      <Button size="sm" variant="outline" onClick={issue} disabled={busy} className="h-7 text-[11px]" data-testid={`issue-letter-${cycle.id}`}>
        <FileText className="h-3 w-3 mr-1" />{busy ? "..." : "Issue Letter"}
      </Button>
    );
  }
  if (hasLetter) {
    return (
      <Button size="sm" variant="outline" onClick={download} className="h-7 text-[11px]" data-testid={`download-letter-${cycle.id}`}>
        <Download className="h-3 w-3 mr-1" />Download
      </Button>
    );
  }
  return <span className="text-[10px] text-slate-400">—</span>;
}

// ---------------------------------------------------------------------------
function MistakesTab() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [mistakes, setMistakes] = useState([]);
  const [form, setForm] = useState({ severity: "minor", occurred_on: new Date().toISOString().slice(0, 10), description: "", cost_impact: "" });

  useEffect(() => { api.get("/employees").then(r => setEmployees(r.data || [])); }, []);
  useEffect(() => {
    if (!selectedEmp) { setMistakes([]); return; }
    api.get(`/appraisal/mistakes/employee/${selectedEmp}`).then(r => setMistakes(r.data || []));
  }, [selectedEmp]);

  const add = async () => {
    if (!selectedEmp) { toast.error("Pick employee first"); return; }
    if (!form.description.trim()) { toast.error("Description required"); return; }
    try {
      await api.post("/appraisal/mistakes", {
        employee_id: selectedEmp, severity: form.severity,
        occurred_on: form.occurred_on, description: form.description,
        cost_impact: form.cost_impact ? parseFloat(form.cost_impact) : null,
      });
      toast.success("Mistake logged");
      setForm({ ...form, description: "", cost_impact: "" });
      const r = await api.get(`/appraisal/mistakes/employee/${selectedEmp}`);
      setMistakes(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this mistake entry?")) return;
    await api.delete(`/appraisal/mistakes/${id}`);
    setMistakes(mistakes.filter(m => m.id !== id));
    toast.success("Deleted");
  };

  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="mistakes-tab">
      <div className="md:col-span-1 bg-amber-50 border border-amber-200 rounded p-4 space-y-3">
        <h3 className="text-sm font-bold text-amber-900">Log a Mistake</h3>
        <div>
          <Label className="text-[11px] uppercase">Employee</Label>
          <Select value={selectedEmp} onValueChange={setSelectedEmp}>
            <SelectTrigger className="h-8 text-xs" data-testid="mistake-emp-select"><SelectValue placeholder="Pick employee" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {employees.map(e => (<SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase">Severity</Label>
          <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="minor">Minor</SelectItem>
              <SelectItem value="major">Major</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase">Occurred On</Label>
          <Input type="date" value={form.occurred_on} onChange={(e) => setForm({ ...form, occurred_on: e.target.value })} className="h-8 text-xs" />
        </div>
        <div>
          <Label className="text-[11px] uppercase">Description</Label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="text-xs" data-testid="mistake-desc" />
        </div>
        <div>
          <Label className="text-[11px] uppercase">Cost Impact ₹ (optional)</Label>
          <Input type="number" value={form.cost_impact} onChange={(e) => setForm({ ...form, cost_impact: e.target.value })} className="h-8 text-xs" />
        </div>
        <Button onClick={add} className="brand-bg brand-bg-hover text-white w-full" data-testid="add-mistake-btn"><Plus className="h-3.5 w-3.5 mr-1" />Log Mistake</Button>
      </div>
      <div className="md:col-span-2 bg-white border border-slate-200 rounded p-4">
        <h3 className="text-sm font-bold mb-2">History {selectedEmp && `(${mistakes.length} entries)`}</h3>
        {!selectedEmp && <p className="text-xs text-slate-400">Pick an employee to view their mistake log.</p>}
        {selectedEmp && mistakes.length === 0 && <p className="text-xs text-slate-400 italic">No mistakes logged — clean record.</p>}
        <div className="space-y-2">
          {mistakes.map(m => (
            <div key={m.id} className="border border-slate-200 rounded p-2.5 hover:bg-red-50/20" data-testid={`mistake-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${m.severity === "critical" ? "bg-red-100 text-red-700" : m.severity === "major" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>{m.severity}</span>
                    <span className="text-xs text-slate-500">{m.occurred_on}</span>
                    {m.acknowledged && <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded">ACK</span>}
                    {m.cost_impact > 0 && <span className="text-[10px] font-mono text-red-600">₹{m.cost_impact}</span>}
                  </div>
                  <p className="text-xs text-slate-800 mt-1">{m.description}</p>
                  <p className="text-[10px] text-slate-400 mt-1">by {m.logged_by_email}</p>
                </div>
                <button onClick={() => del(m.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function AppreciationsTab() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", occurred_on: new Date().toISOString().slice(0, 10) });

  useEffect(() => { api.get("/employees").then(r => setEmployees(r.data || [])); }, []);
  useEffect(() => {
    if (!selectedEmp) { setItems([]); return; }
    api.get(`/appraisal/appreciations/employee/${selectedEmp}`).then(r => setItems(r.data || []));
  }, [selectedEmp]);

  const add = async () => {
    if (!selectedEmp || !form.title.trim()) { toast.error("Pick employee + title"); return; }
    try {
      await api.post("/appraisal/appreciations", { employee_id: selectedEmp, ...form });
      toast.success("Appreciation added");
      setForm({ ...form, title: "", description: "" });
      const r = await api.get(`/appraisal/appreciations/employee/${selectedEmp}`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="appreciations-tab">
      <div className="md:col-span-1 bg-emerald-50 border border-emerald-200 rounded p-4 space-y-3">
        <h3 className="text-sm font-bold text-emerald-900 flex items-center gap-1.5"><Trophy className="h-4 w-4" />Give Kudos</h3>
        <Select value={selectedEmp} onValueChange={setSelectedEmp}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Pick employee" /></SelectTrigger>
          <SelectContent className="max-h-72">
            {employees.map(e => (<SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. Saved client deal)" className="h-8 text-xs" data-testid="appr-title" />
        <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Details (optional)" rows={3} className="text-xs" />
        <Input type="date" value={form.occurred_on} onChange={(e) => setForm({ ...form, occurred_on: e.target.value })} className="h-8 text-xs" />
        <Button onClick={add} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full" data-testid="add-appr-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
      </div>
      <div className="md:col-span-2 bg-white border border-slate-200 rounded p-4">
        <h3 className="text-sm font-bold mb-2">Appreciation Wall {selectedEmp && `(${items.length})`}</h3>
        {!selectedEmp && <p className="text-xs text-slate-400">Pick an employee to view their kudos.</p>}
        <div className="space-y-2">
          {items.map(a => (
            <div key={a.id} className="border border-emerald-100 bg-emerald-50/40 rounded p-2.5">
              <div className="flex items-center gap-1.5">
                <Star className="h-3.5 w-3.5 text-amber-500" />
                <span className="text-xs font-bold text-slate-800">{a.title}</span>
                <span className="ml-auto text-[10px] text-slate-500">{a.occurred_on}</span>
              </div>
              {a.description && <p className="text-xs text-slate-700 mt-1">{a.description}</p>}
              <p className="text-[10px] text-slate-400 mt-1">by {a.given_by_email}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 2 — Management Analytics Dashboard
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [cycle, setCycle] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = cycle ? `?cycle_name=${encodeURIComponent(cycle)}` : "";
      const { data: d } = await api.get(`/appraisal/analytics/overview${q}`);
      setData(d);
    } catch (e) {
      toast.error("Failed to load analytics");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cycle]);

  if (!data) return <div className="text-xs text-slate-400 p-8 text-center" data-testid="analytics-loading">Loading analytics…</div>;

  const COLORS = ["#BE1019", "#105330", "#A7744A", "#888780", "#D2AD78", "#8FC3A3"];
  const statusRows = Object.entries(data.status_counts || {}).map(([k, v]) => ({ name: k.replace(/_/g, " "), value: v }));
  const grades = data.grade_distribution || [];
  const depts = data.by_department || [];
  const mistakes = data.mistake_hotspots || [];
  const kudos = data.appreciation_leaders || [];

  return (
    <div className="space-y-4" data-testid="analytics-tab">
      {/* Filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-xs text-slate-600">Cycle:</Label>
        <Select value={cycle || "all"} onValueChange={(v) => setCycle(v === "all" ? "" : v)}>
          <SelectTrigger className="w-72 h-8 text-xs" data-testid="analytics-cycle-filter">
            <SelectValue placeholder="All Cycles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cycles</SelectItem>
            {(data.cycles_available || []).map(c => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="h-8 text-xs" data-testid="analytics-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh
        </Button>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiTile testid="kpi-total" label="Total Cycles" value={data.overall.total_cycles} tone="slate" />
        <KpiTile testid="kpi-approved" label="Approved" value={data.overall.approved} tone="emerald" />
        <KpiTile testid="kpi-completion" label="Completion %" value={`${data.overall.completion_pct}%`} tone="blue" />
        <KpiTile testid="kpi-score" label="Avg Score" value={data.overall.avg_weighted_score || "—"} tone="violet" />
        <KpiTile testid="kpi-hike" label="Avg Hike %" value={`${data.overall.avg_hike_pct || 0}%`} tone="pink" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Status pie */}
        <div className="bg-white border border-slate-200 rounded p-3" data-testid="chart-status">
          <h4 className="text-xs font-bold text-slate-700 mb-2">Cycle Status Distribution</h4>
          {statusRows.length === 0 ? <p className="text-[11px] text-slate-400 py-8 text-center">No data</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusRows} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={(e) => `${e.name}: ${e.value}`}>
                  {statusRows.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Grade bar */}
        <div className="bg-white border border-slate-200 rounded p-3" data-testid="chart-grades">
          <h4 className="text-xs font-bold text-slate-700 mb-2">Grade Distribution (Approved+)</h4>
          {grades.length === 0 ? <p className="text-[11px] text-slate-400 py-8 text-center">No approved cycles yet</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={grades}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="grade" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="count" name="Count" fill="#10b981" />
                <Bar dataKey="avg_hike_pct" name="Avg Hike %" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Dept-wise table */}
      <div className="bg-white border border-slate-200 rounded p-3" data-testid="chart-departments">
        <h4 className="text-xs font-bold text-slate-700 mb-2">Department-wise Performance</h4>
        {depts.length === 0 ? <p className="text-[11px] text-slate-400 py-6 text-center">No approved cycles yet</p> : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-2 py-1.5">Department</th>
                <th className="text-right px-2 py-1.5">Count</th>
                <th className="text-right px-2 py-1.5">Avg Score</th>
                <th className="text-right px-2 py-1.5">Avg Hike %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {depts.map((d, i) => (
                <tr key={i} data-testid={`dept-row-${i}`}>
                  <td className="px-2 py-1.5">{d.department}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{d.count}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{d.avg_score}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-emerald-700">{d.avg_hike_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Mistake hotspots */}
        <div className="bg-white border border-slate-200 rounded p-3" data-testid="mistake-hotspots">
          <h4 className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1">
            <AlertOctagon className="h-3.5 w-3.5" /> Mistake Hotspots (last 365d)
          </h4>
          {mistakes.length === 0 ? <p className="text-[11px] text-slate-400 py-4">Clean record — no mistakes</p> : (
            <div className="space-y-1.5">
              {mistakes.map(m => (
                <div key={m.employee_id} className="flex items-center justify-between text-xs border-b border-slate-100 pb-1">
                  <span className="font-medium text-slate-800">{m.employee_name}</span>
                  <span className="font-mono">
                    <span className="text-red-600">{m.critical}c</span>{" / "}
                    <span className="text-amber-600">{m.major}m</span>{" / "}
                    <span className="text-slate-700">total {m.total}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Appreciation leaders */}
        <div className="bg-white border border-slate-200 rounded p-3" data-testid="appreciation-leaders">
          <h4 className="text-xs font-bold text-emerald-700 mb-2 flex items-center gap-1">
            <Trophy className="h-3.5 w-3.5" /> Appreciation Leaders (last 365d)
          </h4>
          {kudos.length === 0 ? <p className="text-[11px] text-slate-400 py-4">No appreciations yet</p> : (
            <div className="space-y-1.5">
              {kudos.map(k => (
                <div key={k.employee_id} className="flex items-center justify-between text-xs border-b border-slate-100 pb-1">
                  <span className="font-medium text-slate-800">{k.employee_name}</span>
                  <span className="font-mono text-emerald-700">{k.count} kudos</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, tone = "slate", testid }) {
  const toneMap = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    violet: "bg-violet-50 border-violet-200 text-violet-800",
    pink: "bg-red-50 border-red-200 text-red-800",
  };
  return (
    <div data-testid={testid} className={`border rounded p-3 ${toneMap[tone] || toneMap.slate}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
